import { apiFetch, buildQuery } from "./client";
import type { Page } from "./admin";
import type { AssetCondition, MountState, PathEntry, ReplacementKind } from "./assets";
import type { PositionView } from "./structure";

/** Sprint 6 contract: docs/api/sprint6-layout-pogona.md §2–§3, §7 (layouts with markers) and §9 (3D layouts). */

/** A picture with pixel dimensions, or a glTF model whose parts the markers point at (§9). */
export type LayoutKind = "IMAGE" | "MODEL";

/** One selectable part of a 3D layout's model: the glTF node index (identity) and its name, "" when the export kept none. */
export type ModelPart = { index: number; name: string };

/** PIN = point; ZONE = rectangle; CIRCLE = ellipse box that is a circle in px; POLYGON = free shape (§7); PARTS = a set of model parts (§9). */
export type MarkerShape = "PIN" | "ZONE" | "CIRCLE" | "POLYGON" | "PARTS";
/** Normalised polygon vertex, 6 decimals (§7.2). */
export type MarkerPoint = [number, number];

/** The zones are areas with a bounding box (`w`/`h`); a pin is a point and a set of parts has no geometry at all. */
export function isArea(shape: MarkerShape): boolean {
  return shape === "ZONE" || shape === "CIRCLE" || shape === "POLYGON";
}

/**
 * What a marker shows (§2, §8): the position states plus `INCOMPLETE` — the position has its asset
 * mounted, but a position below it (any depth) is empty. Exists only on layouts.
 */
export type MarkerState = MountState | "INCOMPLETE";

/** Marker counts by state; `incomplete` markers are not counted in `mounted`. */
export type LayoutStateCounts = { mounted: number; incomplete: number; empty: number; temporary: number; none: number };

export type LayoutView = {
  id: string;
  positionId: string;
  positionCode: string | null;
  positionName: string;
  positionKind: string;
  /** First level → the layout's position (included, last). */
  positionPath: PathEntry[];
  name: string;
  sortOrder: number;
  attachmentId: string;
  kind: LayoutKind;
  /** IMAGE: app redirect route under the layout (`/api/layouts/{id}/image`, `layouts:view`), never a presigned URL; null for a MODEL. */
  imageUrl: string | null;
  thumbnailUrl: string | null;
  imageWidth: number | null;
  imageHeight: number | null;
  /** MODEL: `/api/layouts/{id}/model?v={attachmentId}` — streamed through the app, cached by the browser per `v`; null for an IMAGE. */
  modelUrl: string | null;
  modelPartCount: number | null;
  modelTriangles: number | null;
  markerCount: number;
  stateCounts: LayoutStateCounts;
  createdAt: string;
  updatedAt: string;
};

export type MarkerMountedAsset = {
  id: string;
  tag: string;
  typeName: string;
  condition: AssetCondition;
  replacementKind: ReplacementKind | null;
  replacementReminderAt: string | null;
};

export type LayoutMarkerView = {
  id: string;
  layoutId: string;
  positionId: string;
  code: string | null;
  name: string;
  kind: string;
  sequenceNo: number | null;
  /** From the layout's position (excluded) down to the marker's parent; [] for direct children. */
  path: PathEntry[];
  shape: MarkerShape;
  /** Normalised 0..1 against the image width / height. */
  x: number;
  y: number;
  /** Bounding box for every area shape (ZONE / CIRCLE / POLYGON — for a polygon computed by the server); null for a pin. */
  w: number | null;
  h: number | null;
  /** POLYGON only: vertices in drawing order (3..200), otherwise null. */
  points: MarkerPoint[] | null;
  /** PARTS only (§9): indices into the layout's `modelParts`, sorted, unique, may be empty; otherwise null. */
  parts: number[] | null;
  label: string | null;
  /**
   * Own state when the position's kind holds an asset (`INCOMPLETE` = mounted over an empty
   * position), otherwise the aggregate of the positions below (§2, §8).
   */
  state: MarkerState;
  /** Positions below the marker's position (itself excluded, any depth) by own mountState. */
  subtree: { mounted: number; empty: number; temporary: number };
  expectedTypeId: string | null;
  expectedTypeName: string | null;
  mountedAsset: MarkerMountedAsset | null;
  /** First layout (by sortOrder) of the marked position itself — drill-down. */
  ownLayoutId: string | null;
};

export type PlacementMarker = {
  id: string;
  shape: MarkerShape;
  x: number;
  y: number;
  w: number | null;
  h: number | null;
  points: MarkerPoint[] | null;
  parts: number[] | null;
  state: MarkerState;
};

export type PlacementView = {
  layoutId: string;
  layoutName: string;
  layoutPositionId: string;
  layoutPositionName: string;
  layoutPositionCode: string | null;
  kind: LayoutKind;
  /** Picture fields null for a MODEL layout, `modelUrl` null for an IMAGE one (§9). */
  imageUrl: string | null;
  thumbnailUrl: string | null;
  imageWidth: number | null;
  imageHeight: number | null;
  modelUrl: string | null;
  marker: PlacementMarker;
  /** Which position of the chain is marked: the asked one or an ancestor. */
  placedPositionId: string;
  placedPositionName: string;
  /** 0 = the position itself, 1 = its parent, 2 = grandparent… */
  distance: number;
};

export type LayoutDetail = LayoutView & {
  /** MODEL only (§9): every selectable part of the model, in file order; null for an IMAGE. */
  modelParts: ModelPart[] | null;
  /** Sorted ZONE first, then name — zones are drawn under pins. */
  markers: LayoutMarkerView[];
  /** Where the layout's position (or its ancestors) is marked, nearest first. */
  parentPlacements: PlacementView[];
};

/** `name` 1..120. The background decides the kind (picture or GLB); on PUT it must stay of the same kind (§9). */
export type LayoutRequest = { name: string; attachmentId: string; sortOrder?: number };

/** `label` up to 60 characters; `positionId` is ignored on PUT. Coordinates for the geometric shapes, `parts` for PARTS (§9). */
export type LayoutMarkerRequest = {
  positionId: string;
  shape?: MarkerShape;
  x?: number;
  y?: number;
  w?: number | null;
  h?: number | null;
  /** POLYGON only (3..200 points); the server derives `x,y,w,h` from them. */
  points?: MarkerPoint[] | null;
  /** PARTS only: glTF node indices (may be empty); a part taken from another marker of the layout moves. */
  parts?: number[] | null;
  label?: string | null;
};

export type LayoutListParams = { q?: string; page?: number; size?: number };

/** Server limit of the candidate list (§3): default 100, max 300. */
export const CANDIDATES_MAX = 300;

/** Contract §2: coordinates are sent with 6 decimals. */
export function roundCoord(v: number): number {
  return Math.round(Math.min(1, Math.max(0, v)) * 1_000_000) / 1_000_000;
}

export function roundPoints(points: MarkerPoint[]): MarkerPoint[] {
  return points.map(([x, y]) => [roundCoord(x), roundCoord(y)]);
}

/** Default zone size when a pin is turned into a zone (§5: 0.08 around the point, kept inside 0..1). */
export const DEFAULT_ZONE_SIZE = 0.08;

export function pinToZone(m: { x: number; y: number }): { x: number; y: number; w: number; h: number } {
  const w = DEFAULT_ZONE_SIZE;
  const h = DEFAULT_ZONE_SIZE;
  return {
    x: roundCoord(Math.min(Math.max(0, m.x - w / 2), 1 - w)),
    y: roundCoord(Math.min(Math.max(0, m.y - h / 2), 1 - h)),
    w,
    h,
  };
}

/** Centre of an area's bounding box — the pin it turns into. */
export function zoneToPin(m: { x: number; y: number; w: number | null; h: number | null }): { x: number; y: number } {
  return { x: roundCoord(m.x + (m.w ?? 0) / 2), y: roundCoord(m.y + (m.h ?? 0) / 2) };
}

/** The text shown on the layout: `label`, else `code`, else `name` (§5). */
export function markerLabel(m: { label: string | null; code: string | null; name: string; sequenceNo?: number | null }): string {
  if (m.label) return m.label;
  if (m.code) return m.code;
  return m.sequenceNo !== null && m.sequenceNo !== undefined ? `${m.name} #${m.sequenceNo}` : m.name;
}

/** Full PUT body from a marker view (§3: `positionId` ignored on PUT; §7: `points` only for a polygon). */
export function toMarkerRequest(m: LayoutMarkerView): LayoutMarkerRequest {
  if (m.shape === "PARTS") return { positionId: m.positionId, shape: "PARTS", parts: m.parts ?? [], label: m.label };
  const area = isArea(m.shape);
  return {
    positionId: m.positionId,
    shape: m.shape,
    x: m.x,
    y: m.y,
    w: area ? m.w : null,
    h: area ? m.h : null,
    points: m.shape === "POLYGON" ? m.points : null,
    label: m.label,
  };
}

export const layoutsApi = {
  /** Active layouts; `q` ILIKE over name / position name / position code. */
  list: (params: LayoutListParams = {}) =>
    apiFetch<Page<LayoutView>>(`/layouts${buildQuery(params)}`),
  get: (id: string) => apiFetch<LayoutDetail>(`/layouts/${id}`),
  /** Layouts of one position, `sortOrder, name`. */
  forPosition: (positionId: string) => apiFetch<LayoutView[]>(`/positions/${positionId}/layouts`),
  /** 201; 400 VALIDATION (details.attachmentId = NOT_FOUND / UNSUPPORTED_TYPE / WRONG_ENTITY / NOT_READY), UPLOAD_INVALID or MODEL_INVALID (§9). */
  create: (positionId: string, body: LayoutRequest) =>
    apiFetch<LayoutDetail>(`/positions/${positionId}/layouts`, { method: "POST", body }),
  /** Changing `attachmentId` keeps the markers (normalised coordinates). */
  update: (id: string, body: LayoutRequest) =>
    apiFetch<LayoutDetail>(`/layouts/${id}`, { method: "PUT", body }),
  /** 204 soft delete. */
  /** `reason` is mandatory (2026-09-22). */
  remove: (id: string, reason: string) => apiFetch<void>(`/layouts/${id}?reason=${encodeURIComponent(reason)}`, { method: "DELETE" }),
  /** 201; 400 VALIDATION details.positionId = NOT_FOUND / NOT_DESCENDANT / ALREADY_PLACED, details.shape, details.x… */
  addMarker: (layoutId: string, body: LayoutMarkerRequest) =>
    apiFetch<LayoutMarkerView>(`/layouts/${layoutId}/markers`, { method: "POST", body }),
  updateMarker: (layoutId: string, markerId: string, body: LayoutMarkerRequest) =>
    apiFetch<LayoutMarkerView>(`/layouts/${layoutId}/markers/${markerId}`, { method: "PUT", body }),
  /** 204 hard delete (audit DELETE). */
  removeMarker: (layoutId: string, markerId: string) =>
    apiFetch<void>(`/layouts/${layoutId}/markers/${markerId}`, { method: "DELETE" }),
  /** Sub-tree positions not yet on this layout; depth, then `sortOrder, name`. */
  candidates: (layoutId: string, q?: string, limit?: number) =>
    apiFetch<PositionView[]>(`/layouts/${layoutId}/candidates${buildQuery({ q, limit })}`),
  /** Every marker of the position and of its ancestors, `distance` then `layoutName`; [] when none. */
  placements: (positionId: string) => apiFetch<PlacementView[]>(`/positions/${positionId}/placements`),
};
